import { Select, Text } from "@primer/react";
import { Spinner } from "@primer/react";
import { MButton } from "../../../components/mobile";
import { IMyRegistration } from "../../../model/respone/registration/IMyRegistration";
import RegisteredCard from "./RegisteredCard";

interface IRegisteredTabProps {
    /** Danh sách đã lọc theo hình thức (memo `registeredList` của ClassesPage). */
    registeredList: IMyRegistration[];
    myLoading: boolean;
    /** Tổng tín chỉ + ngưỡng min/max. */
    tongTC: number;
    tcMin: number;
    tcMax: number;
    // ----- chốt dữ liệu -----
    isChotDuLieu: boolean;
    isChottingNow: boolean;
    ngayChot: Date | null;
    onChotClick: () => void;
    // ----- hủy -----
    cancellingClassIds: number[];
    gateReason?: string;
    onCancel: (row: IMyRegistration) => void;
    // ----- filter hình thức -----
    hinhThucReg: number;
    onChangeHinhThucReg: (v: number) => void;
}

/**
 * Tab "Đã ĐK" — section #Registered của desktop: tổng TC, lọc hình thức,
 * danh sách thẻ lớp đã đăng ký, nút "Lưu dữ liệu đăng ký" (chốt).
 */
const RegisteredTab = ({
    registeredList,
    myLoading,
    tongTC,
    tcMin,
    tcMax,
    isChotDuLieu,
    isChottingNow,
    ngayChot,
    onChotClick,
    cancellingClassIds,
    gateReason,
    onCancel,
    hinhThucReg,
    onChangeHinhThucReg,
}: IRegisteredTabProps) => {
    return (
        <>
            {/* Tổng tín chỉ */}
            <div className="m-summary">
                <div className="m-summary-row">
                    <span>Số tín chỉ tối thiểu</span>
                    <Text sx={{ fontWeight: 700 }}>{tcMin}</Text>
                </div>
                <div className="m-summary-row">
                    <span>Số tín chỉ tối đa</span>
                    <Text sx={{ fontWeight: 700 }}>{tcMax}</Text>
                </div>
                <div className="m-summary-row">
                    <span>Đã đăng ký</span>
                    <Text sx={{ fontWeight: 700, color: "danger.fg" }}>{tongTC} TC</Text>
                </div>
            </div>

            {/* Cảnh báo chưa lưu — kết quả chưa được công nhận */}
            {!isChotDuLieu && registeredList.length > 0 && (
                <div className="m-warn">
                    <i className="fas fa-triangle-exclamation m-warn-icon" />
                    <div className="m-warn-body">
                        <div className="m-warn-title">Chưa lưu — kết quả chưa được công nhận</div>
                        <div className="m-warn-text">
                            Các lớp bạn đã đăng ký <strong>chưa được lưu</strong>. Hãy bấm{" "}
                            <strong>"Lưu dữ liệu đăng ký"</strong> bên dưới. Nếu không lưu, kết quả
                            đăng ký sẽ <strong>không được công nhận và xét duyệt</strong>.
                        </div>
                    </div>
                </div>
            )}

            {/* Nút Lưu dữ liệu — ẩn sau khi đã chốt */}
            {!isChotDuLieu && (
                <MButton
                    variant="primary"
                    block
                    className="m-btn-pulse"
                    loading={isChottingNow}
                    disabled={registeredList.length === 0 || !!gateReason}
                    title={gateReason || "Lưu dữ liệu đăng ký — sau khi lưu sẽ không thể thay đổi"}
                    onClick={onChotClick}
                    sx={{ mb: 2 }}
                >
                    Lưu dữ liệu đăng ký
                </MButton>
            )}

            {/* Banner sau khi đã chốt */}
            {isChotDuLieu && (
                <div className="m-banner m-banner-success">
                    Bạn đã lưu dữ liệu đăng ký
                    {ngayChot && (
                        <>
                            {" "}vào lúc{" "}
                            <strong>
                                {ngayChot.toLocaleString("vi-VN", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                })}
                            </strong>
                        </>
                    )}
                    . Dữ liệu sau khi lưu sẽ không được thay đổi.
                </div>
            )}

            {/* Lọc hình thức */}
            <div style={{ marginBottom: 10 }}>
                <Select
                    value={String(hinhThucReg)}
                    onChange={e => onChangeHinhThucReg(Number(e.target.value))}
                    aria-label="Lọc theo hình thức đăng ký"
                    block
                    sx={{ minHeight: 44 }}
                >
                    <Select.Option value="0">Tất cả hình thức</Select.Option>
                    <Select.Option value="1">Học chính khóa</Select.Option>
                    <Select.Option value="2">Học lại, cải thiện điểm, học bù (lớp riêng)</Select.Option>
                    <Select.Option value="3">Học lại, cải thiện điểm, học bù (lớp ghép)</Select.Option>
                </Select>
            </div>

            {/* Danh sách lớp đã đăng ký */}
            {myLoading && registeredList.length === 0 ? (
                <div className="m-loading">
                    <Spinner size="medium" />
                    <Text as="p" sx={{ mt: 2, color: "fg.muted" }}>Đang tải...</Text>
                </div>
            ) : registeredList.length === 0 ? (
                <div className="m-empty">
                    <i className="fas fa-clipboard-list" />
                    Bạn chưa đăng ký lớp nào trong đợt này.
                </div>
            ) : (
                registeredList.map(row => (
                    <RegisteredCard
                        key={row.id}
                        row={row}
                        isChotDuLieu={isChotDuLieu}
                        isCancelling={cancellingClassIds.includes(row.id_lop_tc)}
                        gateReason={gateReason}
                        onCancel={onCancel}
                    />
                ))
            )}

            {registeredList.length > 0 && !isChotDuLieu && (
                <div className="m-banner m-banner-warning" style={{ marginTop: 12 }}>
                    <strong>Lưu ý:</strong> Hệ thống sẽ tự đăng xuất khi hết phiên đăng ký — hãy lưu
                    dữ liệu trước khi hết giờ.
                </div>
            )}
        </>
    );
};

export default RegisteredTab;
